/**
 * FolderScanJobContext — app-level manager for a desktop folder-scan ingest.
 *
 * Why this exists: scanning a folder can mean dozens of files. The upload +
 * backend processing must NOT trap the user in the FolderScanSheet. So the sheet
 * just hands the chosen files to `start()` here and closes; this provider runs
 * the work in the background and a floating <FolderScanProgressChip> reflects it.
 *
 * Two phases tracked per job:
 *   uploading  — reading each file (scope-checked in Electron main) and PUTting
 *                it to S3 + creating the document record. `uploaded` counts these.
 *   ingesting  — the backend OCR/extraction worker processes each document
 *                asynchronously and emits an Ably completion event. We reuse
 *                useDocumentChannel to listen and tick `processed` up per doc.
 *
 * A document created as 'active' (rare fast-path types) is counted processed
 * immediately since no Ably completion will follow. One job at a time — starting
 * a new scan replaces the previous (jobSeq invalidates the old loop's callbacks).
 *
 * Mounted inside AblyProvider (see App.tsx) so useDocumentChannel has a client.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../store';
import { useDocumentChannel } from '../hooks/useDocumentChannel';
import { uploadScannedFiles, type UploadFileResult } from '../utilities/diskScan';
import type { ScannedFile } from '../utilities/platform';

export type ScanJobStatus = 'uploading' | 'ingesting' | 'done';

/** Per-file lifecycle: queued → uploading → processing → processed | failed. */
export type ScanFileStatus = 'queued' | 'uploading' | 'processing' | 'processed' | 'failed';

export interface ScanJobFile {
  path: string;
  name: string;
  ext: string;
  folder: string;
  size: number;
  status: ScanFileStatus;
  /** Document id once created — used to match Ably completion events. */
  documentId?: string | null;
}

export interface ScanJob {
  projectId: number | string;
  projectName: string;
  /** Files chosen for this batch. */
  total: number;
  /** Files PUT to S3 + document record created. */
  uploaded: number;
  /** Documents that finished backend processing (or were 'active' on create). */
  processed: number;
  /** Files that failed to upload OR failed processing. */
  failed: number;
  /** Filename currently uploading (for the chip subtitle). */
  currentName: string;
  status: ScanJobStatus;
  /** Per-file rows for the expandable detail view. */
  files: ScanJobFile[];
}

/** Return a new files array with the row at `path` patched. */
function patchByPath(files: ScanJobFile[], path: string, patch: Partial<ScanJobFile>): ScanJobFile[] {
  return files.map((f) => (f.path === path ? { ...f, ...patch } : f));
}

/** Return a new files array with the row whose documentId matches patched. */
function patchByDocId(files: ScanJobFile[], docId: string, patch: Partial<ScanJobFile>): ScanJobFile[] {
  return files.map((f) => (f.documentId != null && String(f.documentId) === docId ? { ...f, ...patch } : f));
}

interface JobContextValue {
  job: ScanJob | null;
  start: (projectId: number | string, projectName: string, files: ScannedFile[]) => void;
  dismiss: () => void;
}

const FolderScanJobContext = createContext<JobContextValue>({
  job: null,
  start: () => {},
  dismiss: () => {},
});

export const useFolderScanJob = () => useContext(FolderScanJobContext);

/** Give backend processing up to this long before the chip declares "done"
 *  with whatever count it reached — guards against a missed Ably completion
 *  leaving the chip spinning forever. */
const INGEST_FALLBACK_MS = 4 * 60 * 1000;

export function FolderScanJobProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const [job, setJob] = useState<ScanJob | null>(null);

  // Doc ids uploaded and awaiting a processing-completion Ably event.
  const pendingIdsRef = useRef<Set<string>>(new Set());
  // Doc ids already counted (dedupe Ably rewind / duplicate deliveries).
  const countedIdsRef = useRef<Set<string>>(new Set());
  // Flips true once the upload loop has finished issuing every file.
  const uploadDoneRef = useRef(false);
  // Monotonic id so a superseded job's async callbacks become no-ops.
  const jobSeqRef = useRef(0);

  const dismiss = useCallback(() => {
    jobSeqRef.current++;            // invalidate any in-flight loop callbacks
    uploadDoneRef.current = false;
    pendingIdsRef.current = new Set();
    countedIdsRef.current = new Set();
    setJob(null);
  }, []);

  const start = useCallback((projectId: number | string, projectName: string, files: ScannedFile[]) => {
    if (!files.length) return;
    const seq = ++jobSeqRef.current;
    pendingIdsRef.current = new Set();
    countedIdsRef.current = new Set();
    uploadDoneRef.current = false;
    setJob({
      projectId,
      projectName,
      total: files.length,
      uploaded: 0,
      processed: 0,
      failed: 0,
      currentName: files[0]?.name ?? '',
      status: 'uploading',
      files: files.map((f) => ({
        path: f.path,
        name: f.name,
        ext: f.ext,
        folder: f.folder,
        size: f.size,
        status: 'queued' as ScanFileStatus,
        documentId: null,
      })),
    });

    const onFileStart = (file: ScannedFile) => {
      if (seq !== jobSeqRef.current) return;
      setJob((j) => (j ? { ...j, currentName: file.name, files: patchByPath(j.files, file.path, { status: 'uploading' }) } : j));
    };

    const onFileResult = (r: UploadFileResult) => {
      if (seq !== jobSeqRef.current) return;
      const path = r.file.path;
      if (!r.success) {
        setJob((j) => (j ? { ...j, failed: j.failed + 1, files: patchByPath(j.files, path, { status: 'failed' }) } : j));
        return;
      }
      const id = r.documentId != null ? String(r.documentId) : null;
      if (r.status === 'active') {
        // No Ably completion will follow — count it processed now.
        if (id) countedIdsRef.current.add(id);
        setJob((j) => (j ? { ...j, uploaded: j.uploaded + 1, processed: j.processed + 1, files: patchByPath(j.files, path, { status: 'processed', documentId: id }) } : j));
      } else {
        if (id) pendingIdsRef.current.add(id);
        setJob((j) => (j ? { ...j, uploaded: j.uploaded + 1, files: patchByPath(j.files, path, { status: 'processing', documentId: id }) } : j));
      }
    };

    uploadScannedFiles(projectId, files, dispatch, undefined, onFileResult, onFileStart)
      .then(() => {
        if (seq !== jobSeqRef.current) return;
        uploadDoneRef.current = true;
        setJob((j) => (j ? { ...j, status: pendingIdsRef.current.size > 0 ? 'ingesting' : 'done' } : j));
      });
  }, [dispatch]);

  // ── Backend processing completions via the documents Ably channel ──────────
  const handleDocEvent = useCallback((doc: any) => {
    const id = doc?.id != null ? String(doc.id) : null;
    if (!id || !pendingIdsRef.current.has(id) || countedIdsRef.current.has(id)) return;
    const succeeded = doc?.status === 'active';
    const failed = !!doc?.failure_code;
    if (!succeeded && !failed) return;          // intermediate update — ignore
    countedIdsRef.current.add(id);
    pendingIdsRef.current.delete(id);
    setJob((j) => {
      if (!j) return j;
      const files = patchByDocId(j.files, id, { status: succeeded ? 'processed' : 'failed' });
      return succeeded ? { ...j, processed: j.processed + 1, files } : { ...j, failed: j.failed + 1, files };
    });
  }, []);

  useDocumentChannel({ projectId: job?.projectId ?? null, onMessage: handleDocEvent });

  // Flip to 'done' once uploads are issued and every doc is accounted for.
  useEffect(() => {
    if (!job || job.status === 'done') return;
    if (uploadDoneRef.current && job.processed + job.failed >= job.total) {
      setJob((j) => (j && j.status !== 'done' ? { ...j, status: 'done' } : j));
    }
  }, [job]);

  // Fallback: don't let a missed completion event spin forever.
  useEffect(() => {
    if (job?.status !== 'ingesting') return;
    const t = setTimeout(() => {
      setJob((j) => (j && j.status === 'ingesting' ? { ...j, status: 'done' } : j));
    }, INGEST_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [job?.status]);

  return (
    <FolderScanJobContext.Provider value={{ job, start, dismiss }}>
      {children}
    </FolderScanJobContext.Provider>
  );
}
